/**
 * Cataloging — Phase 2.
 *
 * Composer (gallery / upload → Gemini Nano Banana → resize to social or
 * e-commerce preset) PLUS a folder sidebar so leads can curate by
 * collection (Bridal AW26, Etsy, Diwali Drop…) and download the entire
 * folder as a single ZIP.
 *
 * Implementation notes:
 * - Folder UX: left sidebar (luxury matte black + gold accents, glass
 *   tile per folder). The "Uncategorized" bucket is always pinned to
 *   the top and represents items with `folder_id == null`.
 * - One folder per item (move semantics). Multi-select on the grid
 *   enables a sticky action bar (Move / Delete selected / Clear).
 * - Compose dropdown defaults to the currently active folder so the
 *   new render lands right where the user is looking.
 * - ZIP is streamed by the backend; the browser downloads via a temp
 *   <a> link with auth via the existing axios cookie.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { renderImageUrl } from "@/lib/imageUrl";
import { getBackendUrl } from "@/lib/backend";
import { formatApiError } from "@/lib/apiError";
import FolderSidebar from "@/components/catalog/FolderSidebar";
import MovePickerMenu from "@/components/catalog/MovePickerMenu";
import FileDrop from "@/components/catalog/FileDrop";
import MultiSceneDrop from "@/components/catalog/MultiSceneDrop";
import BrandModal from "@/components/catalog/BrandModal";
import CatalogLightbox from "@/components/catalog/CatalogLightbox";
import GuidedPlaceDialog from "@/components/GuidedPlaceDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft, Wand2, Download,
  Trash2, Sparkles, Loader2, Folder, FolderOpen,
  Check, CheckSquare, Square, FolderInput, Archive,
  Inbox, Stamp, BookmarkPlus, ChevronDown,
} from "lucide-react";

const BACKEND = getBackendUrl();
const UNCAT_ID = "__uncat__";

export default function CatalogPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [presets, setPresets] = useState({ formats: [], qualities: [], cost_credits: 2 });
  // User-saved custom sizes — `{id, name, width, height, crop, spec}`.
  // Loaded once and refreshed after any save/delete so the chip row
  // updates in-place without a full page reload.
  const [customFormats, setCustomFormats] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [galleryCursor, setGalleryCursor] = useState(null);
  const [galleryHasMore, setGalleryHasMore] = useState(false);
  const [galleryLoadingMore, setGalleryLoadingMore] = useState(false);
  const [folders, setFolders] = useState([]);
  const [uncatCount, setUncatCount] = useState(0);
  const [activeFolder, setActiveFolder] = useState(UNCAT_ID);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Folder UI state
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  // Multi-select state
  const [selected, setSelected] = useState(new Set());
  const [showMovePicker, setShowMovePicker] = useState(false);

  // Compose form state
  const [sceneType, setSceneType] = useState("model");
  const [productType, setProductType] = useState("");        // ring | earring | … (optional)
  const [productDimensions, setProductDimensions] = useState(""); // e.g. "ring size 16 / 17mm inner Ø" — optional
  const [jewellerySource, setJewellerySource] = useState("gallery");
  const [jewelleryId, setJewelleryId] = useState("");
  const [jewelleryFile, setJewelleryFile] = useState(null);
  const [sceneFile, setSceneFile] = useState(null);
  const [sceneFiles, setSceneFiles] = useState([]);          // batch mode (2-5 files)
  const [batchMode, setBatchMode] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [fmt, setFmt] = useState("ig_square");
  const [extraFmts, setExtraFmts] = useState([]);
  const [multiMode, setMultiMode] = useState(false);
  const [quality, setQuality] = useState("high");
  const [renderQuality, setRenderQuality] = useState("high"); // gpt-image-1 compute: low | medium | high
  const [placementMode, setPlacementMode] = useState("auto"); // auto (Gemini decides) | guided (designer places first)
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [guidedPayload, setGuidedPayload] = useState(null); // { jewellery, scenes }
  const [composing, setComposing] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  // Lightbox state — double-click any composed image to zoom in.
  // Stored as an *index* into `items` so arrow-key navigation can flip
  // to the previous/next item without an extra lookup.
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  // Brand + templates (Phase 3 polish)
  const [brand, setBrand] = useState({ has_logo: false, logo_url: null, corner: "br", opacity: 0.7 });
  const [watermarkOn, setWatermarkOn] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");

  // Lookbook layouts — saved Guided-Place placements (layout only).
  // Loaded once at bootstrap; the GuidedPlaceDialog drives save / apply /
  // delete via the callbacks below.
  const [lookbookLayouts, setLookbookLayouts] = useState([]);

  // Final list of format keys to be sent. In single mode it's just [fmt];
  // in multi mode it's the primary + extras (de-duped, primary first).
  const selectedFmts = useMemo(() => {
    if (!multiMode) return [fmt];
    return [fmt, ...extraFmts.filter((k) => k !== fmt)];
  }, [multiMode, fmt, extraFmts]);

  // Total cost preview: batch multiplies (one Gemini per scene), multi-fmt does not.
  const totalCost = useMemo(() => {
    const per = presets.cost_credits || 2;
    const scenes = batchMode ? Math.max(1, sceneFiles.length) : 1;
    return per * scenes;
  }, [presets.cost_credits, batchMode, sceneFiles.length]);

  const sceneInputRef = useRef(null);
  const jewInputRef = useRef(null);

  const activeFolderObj = useMemo(() => {
    if (activeFolder === UNCAT_ID) {
      return { id: UNCAT_ID, name: "Uncategorized", item_count: uncatCount };
    }
    return folders.find((f) => f.id === activeFolder) || null;
  }, [activeFolder, folders, uncatCount]);

  const refreshFolders = useCallback(async () => {
    const r = await axios.get(`${API}/catalog/folders`);
    setFolders(r.data?.folders || []);
    setUncatCount(r.data?.uncategorized?.item_count ?? 0);
  }, []);

  const refreshItems = useCallback(async (folderId = activeFolder) => {
    setItemsLoading(true);
    try {
      const r = await axios.get(`${API}/catalog`, { params: { folder_id: folderId } });
      setItems(r.data?.items || []);
      setSelected(new Set());
    } finally {
      setItemsLoading(false);
    }
  }, [activeFolder]);

  const bootstrap = useCallback(async () => {
    try {
      const [p, g, foldersRes, itemsRes, brandRes, tplRes, cfmtsRes, layoutsRes] = await Promise.all([
        axios.get(`${API}/catalog/formats`),
        axios.get(`${API}/studio/generations?limit=120`),
        axios.get(`${API}/catalog/folders`),
        axios.get(`${API}/catalog`, { params: { folder_id: UNCAT_ID } }),
        axios.get(`${API}/catalog/settings/brand`),
        axios.get(`${API}/catalog/templates`),
        axios.get(`${API}/catalog/custom-formats`),
        axios.get(`${API}/catalog/lookbook-layouts`),
      ]);
      setPresets(p.data);
      setGallery(g.data?.entries || []);
      setGalleryCursor(g.data?.next_cursor || null);
      setGalleryHasMore(!!g.data?.has_more);
      setFolders(foldersRes.data?.folders || []);
      setUncatCount(foldersRes.data?.uncategorized?.item_count ?? 0);
      setItems(itemsRes.data?.items || []);
      setBrand(brandRes.data);
      setTemplates(tplRes.data?.templates || []);
      setCustomFormats(cfmtsRes.data?.formats || []);
      setLookbookLayouts(layoutsRes.data?.layouts || []);
    } catch (err) {
      console.warn("catalog bootstrap failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/signin?next=/catalog"); return; }
    bootstrap();
  }, [authLoading, user, bootstrap, navigate]);

  // Load older gallery entries on demand (cursor pagination). Called by
  // the "Load more" button in the jewellery picker.
  const loadMoreGallery = useCallback(async () => {
    if (galleryLoadingMore || !galleryHasMore || !galleryCursor) return;
    setGalleryLoadingMore(true);
    try {
      const r = await axios.get(`${API}/studio/generations`, {
        params: { limit: 120, cursor: galleryCursor },
      });
      const next = r.data?.entries || [];
      setGallery((prev) => [...prev, ...next]);
      setGalleryCursor(r.data?.next_cursor || null);
      setGalleryHasMore(!!r.data?.has_more);
    } catch (err) {
      console.warn("gallery load more failed", err);
    } finally {
      setGalleryLoadingMore(false);
    }
  }, [galleryCursor, galleryHasMore, galleryLoadingMore]);

  // Switching folders: update active + refetch items in one shot.
  const switchFolder = async (folderId) => {
    setActiveFolder(folderId);
    setItemsLoading(true);
    try {
      const r = await axios.get(`${API}/catalog`, { params: { folder_id: folderId } });
      setItems(r.data?.items || []);
      setSelected(new Set());
    } catch (err) {
      console.warn("folder switch failed", err);
    } finally {
      setItemsLoading(false);
    }
  };

  // ---------- Folder CRUD ----------
  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error("Give the folder a name."); return; }
    try {
      const r = await axios.post(`${API}/catalog/folders`, { name });
      setFolders((prev) => [...prev, r.data]);
      switchFolder(r.data.id);
      setNewFolderName("");
      setShowNewFolder(false);
      toast.success(`Folder “${r.data.name}” created.`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not create folder."));
    }
  };

  const startRename = (folder) => {
    setRenamingId(folder.id);
    setRenameValue(folder.name);
  };

  const commitRename = async (folder) => {
    const name = renameValue.trim();
    if (!name || name === folder.name) {
      setRenamingId(null);
      return;
    }
    try {
      await axios.patch(`${API}/catalog/folders/${folder.id}`, { name });
      setFolders((prev) => prev.map((f) => f.id === folder.id ? { ...f, name } : f));
      setRenamingId(null);
      toast.success("Renamed.");
    } catch (err) {
      toast.error(formatApiError(err, "Rename failed."));
    }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder “${folder.name}”? Images inside will move to Uncategorized.`)) return;
    try {
      const r = await axios.delete(`${API}/catalog/folders/${folder.id}`);
      const moved = r.data?.items_unfiled || 0;
      setFolders((prev) => prev.filter((f) => f.id !== folder.id));
      setUncatCount((c) => c + moved);
      if (activeFolder === folder.id) switchFolder(UNCAT_ID);
      toast.success(`Folder deleted. ${moved} image(s) moved to Uncategorized.`);
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  };

  // ---------- Brand / Templates ----------
  const applyTemplate = (t) => {
    setSceneType(t.scene_type);
    setPrompt(t.prompt || "");
    setFmt(t.fmt);
    setExtraFmts([]);
    setMultiMode(false);
    setQuality(t.quality);
    toast.success(`Template applied: ${t.name}`);
  };

  const saveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) { toast.error("Name the template first."); return; }
    try {
      const r = await axios.post(`${API}/catalog/templates`, {
        name, scene_type: sceneType, prompt, fmt, quality,
      });
      setTemplates((prev) => [...prev, r.data]);
      setTemplateName("");
      setShowSaveTemplate(false);
      toast.success(`Saved “${r.data.name}”.`);
    } catch (err) {
      toast.error(formatApiError(err, "Save failed."));
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template “${t.name}”?`)) return;
    try {
      await axios.delete(`${API}/catalog/templates/${t.id}`);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Template deleted.");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  };

  // ---------- Lookbook layouts (Guided Place) ----------
  // Save the placements currently sitting in the GuidedPlaceDialog so
  // the same x / y / size / rotation can be applied to a future
  // jewellery × scene batch in one click.
  const saveLookbookLayout = async (name, placements) => {
    const cleanName = (name || "").trim();
    if (!cleanName) {
      toast.error("Name the layout first.");
      return null;
    }
    try {
      const r = await axios.post(`${API}/catalog/lookbook-layouts`, {
        name: cleanName,
        placements,
      });
      setLookbookLayouts((prev) => [...prev, r.data]);
      toast.success(`Layout “${r.data.name}” saved.`);
      return r.data;
    } catch (err) {
      toast.error(formatApiError(err, "Save failed."));
      return null;
    }
  };

  const deleteLookbookLayout = async (layout) => {
    if (!window.confirm(`Delete layout “${layout.name}”?`)) return;
    try {
      await axios.delete(`${API}/catalog/lookbook-layouts/${layout.id}`);
      setLookbookLayouts((prev) => prev.filter((x) => x.id !== layout.id));
      toast.success("Layout deleted.");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  };

  const updateBrand = async ({ logo, corner, opacity }) => {
    try {
      const fd = new FormData();
      if (logo) fd.append("logo", logo);
      if (corner) fd.append("corner", corner);
      if (opacity !== undefined && opacity !== null) fd.append("opacity", String(opacity));
      const r = await axios.put(`${API}/catalog/settings/brand`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBrand(r.data);
      toast.success("Brand updated.");
    } catch (err) {
      toast.error(formatApiError(err, "Brand update failed."));
    }
  };

  const removeBrand = async () => {
    if (!window.confirm("Remove your saved logo? Watermark will be turned off.")) return;
    try {
      await axios.delete(`${API}/catalog/settings/brand`);
      setBrand({ has_logo: false, logo_url: null, corner: "br", opacity: 0.7 });
      setWatermarkOn(false);
      toast.success("Logo removed.");
    } catch (err) {
      toast.error(formatApiError(err, "Remove failed."));
    }
  };

  // ---------- Compose ----------
  // When placementMode === "guided" the designer first positions the
  // jewellery onto each scene in the GuidedPlaceDialog. The dialog
  // hands back N pre-composite Blobs which REPLACE the original scenes
  // in the multipart payload. The backend's "guided" branch then runs
  // a short "photoreal-finalise this layout" prompt over each.
  const compose = async (guidedScenesOverride = null) => {
    // Guard against React passing the click event as first arg
    // (onClick={compose} → compose(syntheticEvent)). Only treat the
    // override as scenes if it's actually an iterable array of Blobs.
    const overrideScenes = Array.isArray(guidedScenesOverride) ? guidedScenesOverride : null;
    // ALL composes (single + batch) go through the async-polling
    // batch endpoint. gpt-image-1 can take 50-90s per render and the
    // synchronous /catalog/compose path was racing Cloudflare's 100s
    // edge timeout (524 errors). Routing through compose-batch with
    // job-id polling dodges that entirely.
    const scenesForUpload = overrideScenes
      ? overrideScenes          // pre-composite Blob[] from GuidedPlaceDialog
      : (batchMode && sceneFiles.length > 0 ? sceneFiles : (sceneFile ? [sceneFile] : []));
    const sceneCount = scenesForUpload.length;
    if (sceneCount === 0) {
      toast.error("Upload a model image or background plate.");
      return;
    }
    if (jewellerySource === "gallery" && !jewelleryId) { toast.error("Pick a jewellery render."); return; }
    if (jewellerySource === "upload" && !jewelleryFile) { toast.error("Upload a jewellery image."); return; }
    if (selectedFmts.length === 0) { toast.error("Pick at least one format."); return; }

    // If guided mode + this is the initial click (not the dialog
    // callback), open the placement dialog FIRST. We resume here with
    // the pre-composite blobs after the user confirms.
    if (placementMode === "guided" && !overrideScenes) {
      // For gallery picks, fetch the render's bytes into a Blob so the
      // canvas can render it as an HTMLImageElement (Files only — gallery
      // ids are just strings, not loadable directly).
      let jewBlob = jewelleryFile;
      if (jewellerySource === "gallery") {
        try {
          // Two-step: first get a signed URL (avoids the iframe cookie
          // issue), then fetch the bytes from that URL.
          const sig = await axios.get(
            `${API}/studio/download-url/${jewelleryId}`,
            { timeout: 15000 },
          );
          const imgUrl = sig.data?.url;
          if (!imgUrl) throw new Error("no url");
          const resp = await axios.get(
            imgUrl.startsWith("http") ? imgUrl : `${BACKEND}${imgUrl}`,
            { responseType: "blob", timeout: 30000 },
          );
          jewBlob = resp.data;
        } catch (e) {
          toast.error("Couldn't load the gallery render. Try again.");
          return;
        }
      }
      setGuidedPayload({
        jewellery: jewBlob,
        scenes: scenesForUpload,
      });
      setGuidedOpen(true);
      return;
    }

    setComposing(true);
    try {
      const fd = new FormData();
      fd.append("scene_type", sceneType);
      if (selectedFmts.length === 1) {
        fd.append("fmt", selectedFmts[0]);
      } else {
        fd.append("fmts", selectedFmts.join(","));
      }
      fd.append("quality", quality);
      fd.append("render_quality", renderQuality);
      fd.append("placement_mode", overrideScenes ? "guided" : "auto");
      fd.append("prompt", prompt);
      if (productType) fd.append("product_type", productType);
      if (productDimensions.trim()) fd.append("product_dimensions", productDimensions.trim());
      if (activeFolder && activeFolder !== UNCAT_ID) fd.append("folder_id", activeFolder);
      if (jewellerySource === "gallery") {
        fd.append("jewellery_gen_id", jewelleryId);
      } else {
        fd.append("jewellery", jewelleryFile);
      }
      // Watermark fields — only honored backend-side when user has a logo.
      if (watermarkOn) {
        fd.append("watermark", "true");
        fd.append("wm_corner", brand.corner || "br");
        fd.append("wm_opacity", String(brand.opacity ?? 0.7));
      }

      let r;
      // Always go through the async-polling batch endpoint, even for
      // single-scene composes — keeps everything off Cloudflare's
      // synchronous 100s timeout window.
      for (const blob of scenesForUpload) {
        // Pre-composite Blobs from GuidedPlaceDialog don't have a .name;
        // give them a synthetic filename so multipart forms accept them.
        fd.append("scenes", blob, blob.name || "scene.png");
      }
      // Async pattern: POST returns {job_id} immediately, then we
      // poll /catalog/compose-batch/{job_id} every 2s until the
      // worker finishes. This dodges Cloudflare's 100s edge timeout
      // entirely — generation can take 60-90s without surfacing a 524.
      const submitResp = await axios.post(`${API}/catalog/compose-batch`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // Upload-only timeout. 4 minutes covers slow uplinks with
        // multi-scene Guided Place payloads (≈3-5 MB / scene JPEG).
        // Actual generation runs asynchronously and is polled below.
        timeout: 240000,
      });
      const jobId = submitResp.data?.job_id;
      if (!jobId) throw new Error("Batch submit returned no job_id.");

      // Poll with a safety ceiling of ~10 minutes (300 × 2s).
      let polled = null;
      for (let i = 0; i < 300; i += 1) {
        await new Promise((res) => setTimeout(res, 2000));
        const s = await axios.get(`${API}/catalog/compose-batch/${jobId}`, {
          timeout: 30000,
        });
        polled = s.data;
        if (polled?.status === "done" || polled?.status === "failed") break;
      }
      if (!polled || (polled.status !== "done" && polled.status !== "failed")) {
        throw new Error("Job is still running. Check your gallery in a minute.");
      }
      if (polled.status === "failed") {
        throw new Error(polled.error || "Compose failed.");
      }
      // Normalise to the same envelope the rest of this function expects.
      r = { data: polled };

      // Envelope contract: { items, count, run_id, [credits_spent], [failed_count] }
      const newItems = r.data?.items || [];
      const landed = newItems.filter((it) =>
        (activeFolder === UNCAT_ID && !it.folder_id) ||
        (activeFolder !== UNCAT_ID && it.folder_id === activeFolder),
      );
      if (landed.length) setItems((prev) => [...landed, ...prev]);

      const inFolder = newItems.filter((it) => it.folder_id).length;
      const inUncat = newItems.length - inFolder;
      if (inFolder > 0) {
        const targetFolderId = newItems.find((it) => it.folder_id)?.folder_id;
        setFolders((prev) => prev.map((f) =>
          f.id === targetFolderId ? { ...f, item_count: (f.item_count || 0) + inFolder } : f,
        ));
      }
      if (inUncat > 0) setUncatCount((c) => c + inUncat);

      const n = newItems.length;
      // Server now always returns credits_spent in the polled job
      // payload — fall back to 2 × scene-count just in case.
      const spent = r.data?.credits_spent ?? (2 * Math.max(sceneCount, 1));
      const failedCount = r.data?.failed_count || 0;
      let msg;
      if (sceneCount > 1) {
        msg = `Composed ${n} ${n === 1 ? "scene" : "scenes"} — ${spent} credits.`;
        if (failedCount > 0) msg += ` (${failedCount} failed, not charged)`;
      } else if (n === 1) {
        msg = "Composed! Check your catalog below.";
      } else {
        msg = `Composed in ${n} formats — ${spent} credits.`;
      }
      toast.success(msg, { duration: 4500 });

      // Reset transient inputs.
      setSceneFile(null);
      setSceneFiles([]);
      setPrompt("");
      if (sceneInputRef.current) sceneInputRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err, "Compose failed."));
    } finally {
      setComposing(false);
    }
  };

  // ---------- Selection / bulk ops ----------
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(items.map((i) => i.id)));

  const moveSelectedTo = async (targetFolderId) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await axios.post(`${API}/catalog/items/move`, {
        item_ids: ids,
        folder_id: targetFolderId === UNCAT_ID ? null : targetFolderId,
      });
      toast.success(`Moved ${ids.length} item(s).`);
      setShowMovePicker(false);
      // Refresh counts + grid.
      await Promise.all([refreshFolders(), refreshItems()]);
    } catch (err) {
      toast.error(formatApiError(err, "Move failed."));
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} catalog image(s)? Can't be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => axios.delete(`${API}/catalog/${id}`)));
      toast.success("Deleted.");
      await Promise.all([refreshFolders(), refreshItems()]);
    } catch (err) {
      toast.error(formatApiError(err, "Bulk delete failed."));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this catalog image? Can't be undone.")) return;
    try {
      await axios.delete(`${API}/catalog/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (item.folder_id) {
        setFolders((prev) => prev.map((f) =>
          f.id === item.folder_id ? { ...f, item_count: Math.max(0, (f.item_count || 1) - 1) } : f,
        ));
      } else {
        setUncatCount((c) => Math.max(0, c - 1));
      }
      toast.success("Deleted.");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  };

  // ---------- ZIP download ----------
  const downloadFolderZip = async () => {
    if (!activeFolderObj || activeFolderObj.item_count === 0) {
      toast.error("This folder is empty — nothing to download.");
      return;
    }
    setDownloadingZip(true);
    try {
      const res = await axios.get(`${API}/catalog/folders/${activeFolder}/zip`, {
        responseType: "blob",
        timeout: 180000,
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const fname = `facetra-catalog-${(activeFolderObj.name || "folder").replace(/[^a-zA-Z0-9._-]+/g, "-")}.zip`;
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const included = res.headers["x-items-included"] || activeFolderObj.item_count;
      toast.success(`Downloaded ${included} image(s) as ZIP.`);
    } catch (err) {
      let detail = "Download failed.";
      if (err?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await err.response.data.text())?.detail || detail; }
        catch { /* ignore */ }
      } else {
        detail = err?.response?.data?.detail || err?.message || detail;
      }
      toast.error(String(detail));
    } finally {
      setDownloadingZip(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#070707] flex items-center justify-center text-white/55 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin text-[#D4AF37]" /> Loading catalog…
      </div>
    );
  }

  const allSelected = items.length > 0 && selected.size === items.length;
  const hasSelection = selected.size > 0;

  return (
    <div className="min-h-screen bg-[#070707] text-white px-6 py-10 sm:px-10">
      <button
        onClick={() => navigate("/app")}
        className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] mb-6"
        data-testid="catalog-back-btn"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> Back to studio
      </button>

      <div className="max-w-7xl">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
          <Sparkles className="w-3 h-3" /> Cataloging · Phase 3
        </div>
        <h1 className="font-display text-3xl text-white mt-1">Catalog &amp; promo shoots</h1>
        <p className="text-sm text-white/55 mt-1 max-w-2xl">
          Drop a piece on a model or against a creative background. Organise into folders,
          download as ZIP, and now <span className="text-white/80">tick multiple formats</span> to
          deliver Amazon · Instagram · Pinterest in one click — still {presets.cost_credits} credits per compose.
        </p>

        {/* Composer card */}
        <ComposerCard
          presets={presets}
          gallery={gallery}
          galleryHasMore={galleryHasMore}
          galleryLoadingMore={galleryLoadingMore}
          onLoadMoreGallery={loadMoreGallery}
          sceneType={sceneType} setSceneType={setSceneType}
          productType={productType} setProductType={setProductType}
          productDimensions={productDimensions} setProductDimensions={setProductDimensions}
          jewellerySource={jewellerySource} setJewellerySource={setJewellerySource}
          jewelleryId={jewelleryId} setJewelleryId={setJewelleryId}
          jewelleryFile={jewelleryFile} setJewelleryFile={setJewelleryFile}
          sceneFile={sceneFile} setSceneFile={setSceneFile}
          sceneFiles={sceneFiles} setSceneFiles={setSceneFiles}
          batchMode={batchMode} setBatchMode={setBatchMode}
          prompt={prompt} setPrompt={setPrompt}
          fmt={fmt} setFmt={setFmt}
          extraFmts={extraFmts} setExtraFmts={setExtraFmts}
          multiMode={multiMode} setMultiMode={setMultiMode}
          selectedFmts={selectedFmts}
          quality={quality} setQuality={setQuality}
          renderQuality={renderQuality} setRenderQuality={setRenderQuality}
          placementMode={placementMode} setPlacementMode={setPlacementMode}
          composing={composing}
          activeFolderObj={activeFolderObj}
          sceneInputRef={sceneInputRef}
          jewInputRef={jewInputRef}
          onCompose={compose}
          templates={templates}
          onApplyTemplate={applyTemplate}
          onDeleteTemplate={deleteTemplate}
          showSaveTemplate={showSaveTemplate}
          setShowSaveTemplate={setShowSaveTemplate}
          templateName={templateName}
          setTemplateName={setTemplateName}
          onSaveTemplate={saveAsTemplate}
          brand={brand}
          watermarkOn={watermarkOn}
          setWatermarkOn={setWatermarkOn}
          onOpenBrandModal={() => setShowBrandModal(true)}
          totalCost={totalCost}
          customFormats={customFormats}
          onSaveCustomFormat={async (payload) => {
            // POST and prepend so the new chip appears immediately
            // without a refetch flash.
            const r = await axios.post(`${API}/catalog/custom-formats`, payload);
            const saved = r.data?.format;
            if (saved) setCustomFormats((prev) => [saved, ...prev]);
            return saved;
          }}
          onDeleteCustomFormat={async (id) => {
            await axios.delete(`${API}/catalog/custom-formats/${id}`);
            setCustomFormats((prev) => prev.filter((c) => c.id !== id));
            // If the deleted size was currently selected, drop it from
            // primary and extra selections so the compose payload stays valid.
            setFmt((f) => (f === customFormats.find((c) => c.id === id)?.spec ? "ig_square" : f));
            setExtraFmts((prev) => prev.filter((k) => k !== customFormats.find((c) => c.id === id)?.spec));
          }}
        />

        {/* Layout: sidebar + grid */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
          <FolderSidebar
            folders={folders}
            uncatCount={uncatCount}
            activeFolder={activeFolder}
            setActiveFolder={switchFolder}
            showNewFolder={showNewFolder} setShowNewFolder={setShowNewFolder}
            newFolderName={newFolderName} setNewFolderName={setNewFolderName}
            onCreateFolder={createFolder}
            renamingId={renamingId} renameValue={renameValue}
            setRenameValue={setRenameValue}
            onStartRename={startRename}
            onCommitRename={commitRename}
            onCancelRename={() => setRenamingId(null)}
            onDeleteFolder={deleteFolder}
          />

          <section data-testid="catalog-folder-pane">
            {/* Folder header */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Viewing</div>
                <h2 className="font-display text-xl text-white mt-0.5 flex items-center gap-2" data-testid="catalog-active-folder-name">
                  {activeFolder === UNCAT_ID
                    ? <Inbox className="w-4 h-4 text-[#D4AF37]" />
                    : <FolderOpen className="w-4 h-4 text-[#D4AF37]" />}
                  {activeFolderObj?.name || "Folder"}
                  <span className="text-xs text-white/40 font-sans">&nbsp;· {activeFolderObj?.item_count ?? items.length} item(s)</span>
                </h2>
              </div>
              <button
                onClick={downloadFolderZip}
                disabled={downloadingZip || (activeFolderObj?.item_count ?? 0) === 0}
                data-testid="catalog-download-zip-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {downloadingZip ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                {downloadingZip ? "Zipping…" : "Download as ZIP"}
              </button>
            </div>

            {/* Selection toolbar */}
            {items.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap text-xs text-white/55 mb-3" data-testid="catalog-selection-bar">
                <button
                  onClick={allSelected ? clearSelection : selectAll}
                  className="inline-flex items-center gap-1.5 hover:text-white"
                  data-testid="catalog-select-all-btn"
                >
                  {allSelected ? <CheckSquare className="w-3.5 h-3.5 text-[#D4AF37]" /> : <Square className="w-3.5 h-3.5" />}
                  {allSelected ? "Clear all" : "Select all"}
                </button>
                {hasSelection && (
                  <>
                    <span className="text-white/35">·</span>
                    <span data-testid="catalog-selection-count">{selected.size} selected</span>
                    <button
                      onClick={() => setShowMovePicker((v) => !v)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 hover:border-[#D4AF37]/45 hover:text-white relative"
                      data-testid="catalog-move-selected-btn"
                    >
                      <FolderInput className="w-3.5 h-3.5" /> Move to folder
                    </button>
                    <button
                      onClick={deleteSelected}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/30 px-3 py-1 text-rose-300 hover:bg-rose-500/15"
                      data-testid="catalog-delete-selected-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                    {showMovePicker && (
                      <MovePickerMenu
                        folders={folders}
                        activeFolder={activeFolder}
                        onPick={moveSelectedTo}
                        onClose={() => setShowMovePicker(false)}
                      />
                    )}
                  </>
                )}
              </div>
            )}

            {/* Grid */}
            {itemsLoading ? (
              <div className="rounded-xl border border-white/10 bg-[#0A0A0A] py-12 text-center text-sm text-white/45">
                <Loader2 className="w-4 h-4 inline mr-2 animate-spin text-[#D4AF37]" /> Loading items…
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/10 bg-[#0A0A0A] py-12 text-center text-sm text-white/45" data-testid="catalog-empty">
                {activeFolder === UNCAT_ID
                  ? "Nothing here yet — compose your first catalog image above."
                  : "This folder is empty. Select images from another folder and move them here, or compose new ones while this folder is active."}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="catalog-grid">
                {items.map((item, idx) => {
                  const isSel = selected.has(item.id);
                  return (
                    <div
                      key={item.id}
                      data-testid={`catalog-item-${item.id}`}
                      onDoubleClick={() => setLightboxIndex(idx)}
                      title="Double-click to zoom"
                      className={`relative aspect-square rounded-lg overflow-hidden border group bg-[#0A0A0A] transition cursor-zoom-in ${isSel ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/40" : "border-white/10"}`}
                    >
                      <img
                        src={`${BACKEND}${item.image_url}`}
                        alt={item.format_label}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />
                      <span className="absolute bottom-1.5 left-2 text-[9.5px] uppercase tracking-[0.16em] text-[#D4AF37] font-medium leading-none">
                        {item.format_label} · {item.width}×{item.height}
                      </span>

                      {/* Hover hint: double-click to zoom */}
                      <span className="absolute bottom-1.5 right-2 text-[8.5px] uppercase tracking-[0.18em] text-white/55 bg-black/55 px-1.5 py-0.5 rounded-full border border-white/10 opacity-0 group-hover:opacity-100 transition pointer-events-none">
                        Dbl-click ⤢
                      </span>

                      {/* Select checkbox (top-left) */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
                        data-testid={`catalog-item-${item.id}-select`}
                        title={isSel ? "Unselect" : "Select"}
                        className={`absolute top-1.5 left-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full transition ${isSel ? "bg-[#D4AF37] text-black opacity-100" : "bg-black/70 border border-white/30 text-white/70 opacity-0 group-hover:opacity-100"}`}
                      >
                        {isSel ? <Check className="w-3 h-3" /> : <span className="w-2.5 h-2.5 rounded-full border border-white/60" />}
                      </button>

                      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                        <a
                          href={`${BACKEND}${item.image_url}?download=1`}
                          download={`${item.id}.${item.file_format || "png"}`}
                          data-testid={`catalog-item-${item.id}-download`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition"
                          title="Download"
                        >
                          <Download className="w-3 h-3" />
                        </a>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                          data-testid={`catalog-item-${item.id}-delete`}
                          title="Delete"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 border border-rose-400/35 text-rose-300 hover:bg-rose-500/25 hover:text-white transition"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {showBrandModal && (
        <BrandModal
          brand={brand}
          onClose={() => setShowBrandModal(false)}
          onUpdate={updateBrand}
          onRemove={removeBrand}
        />
      )}

      {lightboxIndex >= 0 && items[lightboxIndex] && (
        <CatalogLightbox
          items={items}
          index={lightboxIndex}
          backendUrl={BACKEND}
          onClose={() => setLightboxIndex(-1)}
          onNavigate={(i) => setLightboxIndex(i)}
          onRetried={(newItem, originalId) => {
            // Insert the retry result immediately after the original so
            if (!newItem) return;
            setItems((prev) => {
              const idx = prev.findIndex((x) => x.id === originalId);
              if (idx < 0) return [newItem, ...prev];
              const next = [...prev];
              next.splice(idx + 1, 0, newItem);
              return next;
            });
            const targetIdx = items.findIndex((x) => x.id === originalId);
            if (targetIdx >= 0) setLightboxIndex(targetIdx + 1);
          }}
        />
      )}

      <GuidedPlaceDialog
        open={guidedOpen}
        onOpenChange={(v) => {
          setGuidedOpen(v);
          if (!v) setGuidedPayload(null);
        }}
        jewelleryFile={guidedPayload?.jewellery}
        sceneFiles={guidedPayload?.scenes || []}
        savedLayouts={lookbookLayouts}
        onSaveLayout={saveLookbookLayout}
        onDeleteLayout={deleteLookbookLayout}
        onConfirm={async (composites) => {
          // Pre-composite Blobs replace the original scenes. Re-enter
          // the compose flow with placement_mode=guided.
          await compose(composites);
        }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Composer card (kept inline so it can read every form state slice without
// prop-drilling fatigue, but separated to keep the main component readable).
// ---------------------------------------------------------------------------
function ComposerCard({
  presets, gallery,
  galleryHasMore, galleryLoadingMore, onLoadMoreGallery,
  sceneType, setSceneType,
  productType, setProductType,
  productDimensions, setProductDimensions,
  jewellerySource, setJewellerySource,
  jewelleryId, setJewelleryId,
  jewelleryFile, setJewelleryFile,
  sceneFile, setSceneFile,
  sceneFiles, setSceneFiles,
  batchMode, setBatchMode,
  prompt, setPrompt,
  fmt, setFmt,
  extraFmts, setExtraFmts,
  multiMode, setMultiMode,
  selectedFmts,
  quality, setQuality,
  renderQuality, setRenderQuality,
  placementMode, setPlacementMode,
  composing, activeFolderObj,
  sceneInputRef, jewInputRef,
  onCompose,
  templates, onApplyTemplate, onDeleteTemplate,
  showSaveTemplate, setShowSaveTemplate,
  templateName, setTemplateName, onSaveTemplate,
  brand, watermarkOn, setWatermarkOn, onOpenBrandModal,
  totalCost,
  customFormats = [],
  onSaveCustomFormat,
  onDeleteCustomFormat,
}) {
  const maxFmts = presets.max_formats_per_run || 5;
  const [tplOpen, setTplOpen] = useState(false);
  // Custom-size inline form
  const [customOpen, setCustomOpen] = useState(false);
  const [customW, setCustomW] = useState("");
  const [customH, setCustomH] = useState("");
  const [customName, setCustomName] = useState("");
  const [customSavePending, setCustomSavePending] = useState(false);

  const toggleExtraFmt = (key) => {
    if (key === fmt) return; // primary is always included
    setExtraFmts((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length + 1 + 1 > maxFmts) return prev;
      return [...prev, key];
    });
  };

  const onScenesPicked = (files) => {
    const arr = Array.from(files || []).filter((f) => f && f.type?.startsWith("image/"));
    if (arr.length === 0) {
      setSceneFiles([]);
      if (batchMode) setBatchMode(false);
      return;
    }
    if (arr.length === 1) {
      // One file dropped → revert to single-scene mode.
      setSceneFile(arr[0]);
      setSceneFiles([]);
      setBatchMode(false);
      return;
    }
    // 2+ files → batch mode. Multi-format remains user-controlled —
    // each scene gets resized to every selected format (free resizes).
    const capped = arr.slice(0, 5);
    if (arr.length > 5) toast.error(`Max 5 scenes per batch. Took the first 5.`);
    setSceneFiles(capped);
    setSceneFile(null);
    setBatchMode(true);
  };

  const removeSceneAt = (idx) => {
    setSceneFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length < 2) {
        setBatchMode(false);
        if (next.length === 1) setSceneFile(next[0]);
        return [];
      }
      return next;
    });
  };
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0E0E0E] p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="catalog-composer">
      {/* Top utility row: templates + brand — spans both columns */}
      <div className="lg:col-span-2 flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-white/5">
        {/* Templates dropdown */}
        <div className="relative" data-testid="catalog-templates-row">
          <button
            type="button"
            onClick={() => setTplOpen((v) => !v)}
            data-testid="catalog-templates-toggle"
            className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white border border-white/10 rounded-full px-3 py-1.5 hover:border-[#D4AF37]/40 transition"
          >
            <BookmarkPlus className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>Templates</span>
            <span className="text-[10px] text-white/40 tabular-nums">· {templates.length}</span>
            <ChevronDown className={`w-3 h-3 transition ${tplOpen ? "rotate-180" : ""}`} />
          </button>
          {tplOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setTplOpen(false)} />
              <div
                className="absolute z-20 mt-1.5 left-0 w-72 rounded-lg border border-white/10 bg-[#0E0E0E] shadow-2xl p-2 max-h-72 overflow-y-auto"
                data-testid="catalog-templates-menu"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 px-2 pt-1 pb-1.5">
                  Apply a template
                </div>
                {templates.length === 0 && (
                  <div className="text-[11px] text-white/40 px-2 py-2">No templates yet — save one from a setup you like.</div>
                )}
                {templates.map((t) => (
                  <div key={t.id} className="group/tpl flex items-stretch rounded hover:bg-white/[0.04]">
                    <button
                      type="button"
                      onClick={() => { onApplyTemplate(t); setTplOpen(false); }}
                      data-testid={`catalog-template-${t.id}`}
                      className="flex-1 text-left text-xs px-2 py-1.5 text-white/85"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{t.name}</span>
                        {t.starter && (
                          <span className="text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]/85">starter</span>
                        )}
                      </div>
                      <div className="text-[10px] text-white/40 mt-0.5">
                        {t.scene_type === "model" ? "Model" : "Background"} · {t.fmt}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteTemplate(t)}
                      data-testid={`catalog-template-${t.id}-delete`}
                      className="px-2 text-white/35 hover:text-rose-300 opacity-0 group-hover/tpl:opacity-100"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <div className="border-t border-white/5 mt-1.5 pt-1.5">
                  <button
                    type="button"
                    onClick={() => { setShowSaveTemplate(true); setTplOpen(false); }}
                    data-testid="catalog-template-save-toggle"
                    className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-white/[0.05] text-[#D4AF37] inline-flex items-center gap-1.5"
                  >
                    <BookmarkPlus className="w-3.5 h-3.5" /> Save current setup as template…
                  </button>
                </div>
              </div>
            </>
          )}
          {showSaveTemplate && (
            <div className="absolute z-20 mt-1.5 left-0 w-72 rounded-lg border border-[#D4AF37]/40 bg-[#0E0E0E] shadow-2xl p-3" data-testid="catalog-template-save-form">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] mb-1.5">Save as template</div>
              <input
                autoFocus
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onSaveTemplate(); if (e.key === "Escape") { setShowSaveTemplate(false); setTemplateName(""); } }}
                maxLength={60}
                placeholder="e.g. White BG · Amazon"
                data-testid="catalog-template-name-input"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
              />
              <div className="text-[10.5px] text-white/45 mt-1.5">Saves: scene type, prompt, format, quality.</div>
              <div className="mt-2 flex gap-1.5">
                <button
                  onClick={onSaveTemplate}
                  data-testid="catalog-template-save-confirm"
                  className="flex-1 text-xs rounded-md bg-[#D4AF37] text-black py-1.5 hover:bg-[#F3CE5A]"
                >
                  Save
                </button>
                <button
                  onClick={() => { setShowSaveTemplate(false); setTemplateName(""); }}
                  className="text-xs rounded-md border border-white/10 text-white/55 px-3 py-1.5 hover:text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Watermark toggle + brand settings */}
        <div className="flex items-center gap-2" data-testid="catalog-brand-row">
          <button
            type="button"
            onClick={() => {
              if (!brand.has_logo) { onOpenBrandModal(); return; }
              setWatermarkOn((v) => !v);
            }}
            data-testid="catalog-watermark-toggle"
            className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border transition ${
              watermarkOn
                ? "border-[#D4AF37]/55 bg-[#D4AF37]/12 text-white"
                : "border-white/10 text-white/65 hover:text-white hover:border-[#D4AF37]/40"
            }`}
            title={brand.has_logo ? "Toggle watermark for this compose" : "Add your brand logo first"}
          >
            <Stamp className="w-3.5 h-3.5 text-[#D4AF37]" />
            {brand.has_logo
              ? (watermarkOn ? "Watermark · ON" : "Watermark · OFF")
              : "Add brand logo"}
          </button>
          {brand.has_logo && (
            <button
              type="button"
              onClick={onOpenBrandModal}
              data-testid="catalog-brand-settings-btn"
              className="text-xs text-white/45 hover:text-[#D4AF37] underline-offset-2 hover:underline"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-2">Scene</label>
          <div className="inline-flex p-0.5 rounded-full border border-white/10 bg-[#141414]">
            {[
              { v: "model", label: "On a model" },
              { v: "background", label: "Creative background" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setSceneType(opt.v)}
                data-testid={`catalog-scene-${opt.v}`}
                className={`text-xs px-3.5 py-1.5 rounded-full transition ${sceneType === opt.v ? "bg-[#D4AF37] text-black" : "text-white/55 hover:text-white"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] uppercase tracking-[0.22em] text-white/45">
              {sceneType === "model" ? "Model image" : "Background image"}
            </label>
            <span className="text-[10px] text-white/35">
              {batchMode ? `Batch · ${sceneFiles.length}/5` : "Drop 2+ for batch"}
            </span>
          </div>
          <MultiSceneDrop
            singleFile={sceneFile}
            files={sceneFiles}
            batchMode={batchMode}
            onPick={onScenesPicked}
            onRemoveAt={removeSceneAt}
            inputRef={sceneInputRef}
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-2">Jewellery</label>

          {/* Product type — tells Gemini exactly where the piece sits
              (ear vs neck vs finger). Optional, but dramatically reduces
              placement drift when set. Sent as `product_type` form field
              and woven into the compose prompt server-side. */}
          <div className="mb-2.5">
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/40 block mb-1">Product type (optional)</label>
            <Select
              value={productType || "none"}
              onValueChange={(v) => setProductType(v === "none" ? "" : v)}
            >
              <SelectTrigger
                data-testid="catalog-product-type-select"
                className="bg-[#141414] border-white/10 text-white/85 h-9 text-sm hover:border-white/25 focus:border-[#D4AF37]/60 focus:ring-[#D4AF37]/20"
              >
                <SelectValue placeholder="Auto-detect" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 text-white max-h-[420px]">
                <SelectItem value="none" data-testid="catalog-product-type-none" className="text-white/70 focus:bg-white/10 focus:text-white">Auto-detect (let Gemini decide)</SelectItem>
                {[
                  ["ring", "Ring"],
                  ["earring", "Earring"],
                  ["pendant", "Pendant"],
                  ["necklace", "Necklace"],
                  ["choker", "Choker"],
                  ["haar", "Haar (long necklace)"],
                  ["chain", "Chain"],
                  ["bangle", "Bangle"],
                  ["bracelet", "Bracelet"],
                  ["kada", "Kada"],
                  ["nose_pin", "Nose pin / Nath"],
                  ["maang_tikka", "Maang tikka"],
                  ["jhumka", "Jhumka"],
                  ["anklet", "Anklet / Payal"],
                  ["mangalsutra", "Mangalsutra"],
                  ["brooch", "Brooch"],
                  ["waist_belt", "Kamarbandh (waist belt)"],
                ].map(([v, l]) => (
                  <SelectItem
                    key={v}
                    value={v}
                    data-testid={`catalog-product-type-${v}`}
                    className="text-white/85 focus:bg-white/10 focus:text-white data-[state=checked]:text-[#D4AF37]"
                  >
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Product dimensions — pairs with anatomical reference data on
              the backend so Gemini does proportional scaling instead of
              guessing. Especially useful for hand/wrist pieces (rings,
              kadas) which were the most-complained-about mis-scalings. */}
          <div className="mb-2.5">
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/40 block mb-1">
              Product dimensions (optional, for accurate scaling)
            </label>
            <input
              type="text"
              value={productDimensions}
              onChange={(e) => setProductDimensions(e.target.value)}
              data-testid="catalog-product-dimensions"
              placeholder={
                productType === "ring"   ? 'e.g. "ring size 16 / 17mm inner Ø"' :
                productType === "kada"   ? 'e.g. "kada 65mm inner Ø, 12mm metal width"' :
                productType === "bangle" ? 'e.g. "2-6 size / 62mm inner Ø"' :
                productType === "earring"? 'e.g. "10mm stud" or "45mm chandelier drop"' :
                productType === "necklace"? 'e.g. "450mm princess length"' :
                'e.g. "17mm inner Ø", "45mm drop", "65mm inner Ø", etc.'
              }
              maxLength={240}
              className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-1.5 text-[12px] text-white/90 placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/55 hover:border-white/25 transition"
            />
            <div className="text-[9px] uppercase tracking-[0.16em] text-white/30 mt-1">
              Anatomy-aware fit: real dimensions + body-part averages →
              correct on-model scale.
            </div>
          </div>

          <div className="inline-flex p-0.5 rounded-full border border-white/10 bg-[#141414] mb-2">
            {[
              { v: "gallery", label: "From gallery" },
              { v: "upload",  label: "Upload" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setJewellerySource(opt.v)}
                data-testid={`catalog-jewellery-source-${opt.v}`}
                className={`text-xs px-3.5 py-1.5 rounded-full transition ${jewellerySource === opt.v ? "bg-[#D4AF37] text-black" : "text-white/55 hover:text-white"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {jewellerySource === "gallery" ? (
            gallery.length === 0 ? (
              <p className="text-[11px] text-white/45">No renders in your gallery yet. Switch to Upload, or create renders first.</p>
            ) : (
              <div data-testid="catalog-jewellery-grid-wrap">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-96 overflow-y-auto pr-1" data-testid="catalog-jewellery-grid">
                  {gallery.map((g) => (
                    <button
                      key={g.id}
                      onClick={() => setJewelleryId(g.id)}
                      data-testid={`catalog-jewellery-tile-${g.id}`}
                      style={{ aspectRatio: "1 / 1" }}
                      className={`relative rounded-lg overflow-hidden border transition ${jewelleryId === g.id ? "border-[#D4AF37]/85 ring-2 ring-[#D4AF37]/35" : "border-white/10 hover:border-white/30"}`}
                    >
                      <img src={renderImageUrl(BACKEND, g)} alt={g.prompt} className="absolute inset-0 w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-white/40 tabular-nums" data-testid="catalog-jewellery-count">
                    {gallery.length} render{gallery.length === 1 ? "" : "s"}{galleryHasMore ? " · more available" : ""}
                  </span>
                  {galleryHasMore && (
                    <button
                      type="button"
                      onClick={onLoadMoreGallery}
                      disabled={galleryLoadingMore}
                      data-testid="catalog-jewellery-load-more"
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-[#F3CE5A] disabled:opacity-50 transition"
                    >
                      {galleryLoadingMore && <Loader2 className="w-3 h-3 animate-spin" />}
                      {galleryLoadingMore ? "Loading…" : "Load more"}
                    </button>
                  )}
                </div>
              </div>
            )
          ) : (
            <FileDrop file={jewelleryFile} onPick={setJewelleryFile} inputRef={jewInputRef} testId="catalog-jewellery-upload" />
          )}
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Direction (optional)</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            maxLength={400}
            placeholder="e.g. soft natural lighting, golden hour, slight smile, traditional bridal look"
            data-testid="catalog-prompt"
            className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                {multiMode ? "Primary format" : "Format"}
              </label>
              <button
                type="button"
                onClick={() => setMultiMode((v) => !v)}
                data-testid="catalog-multi-toggle"
                className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-[#F3CE5A] transition"
                title={multiMode ? "Switch back to single format" : "Deliver in multiple formats — same 2 credits"}
              >
                {multiMode ? "Single" : "+ Add more"}
              </button>
            </div>
            <select
              value={fmt}
              onChange={(e) => {
                const next = e.target.value;
                setFmt(next);
                // If the new primary was already an "extra", drop it from extras.
                setExtraFmts((prev) => prev.filter((k) => k !== next));
              }}
              data-testid="catalog-format-select"
              className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
            >
              {presets.formats.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} · {p.w}×{p.h}
                </option>
              ))}
              {customFormats.length > 0 && (
                <optgroup label="Your saved sizes">
                  {customFormats.map((c) => (
                    <option key={c.id} value={c.spec}>
                      {c.name} · {c.width}×{c.height}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              type="button"
              onClick={() => setCustomOpen((v) => !v)}
              data-testid="catalog-custom-size-toggle"
              className="mt-1.5 text-[10.5px] text-[#D4AF37] hover:text-[#F3CE5A] transition uppercase tracking-[0.18em]"
            >
              {customOpen ? "Close custom size" : "+ Custom size"}
            </button>
            {customOpen && (
              <div data-testid="catalog-custom-size-panel" className="mt-2 p-2.5 rounded-md border border-white/10 bg-[#0F0F0F] space-y-2">
                <p className="text-[10.5px] text-white/45 leading-snug">
                  Enter width and height in pixels (32&ndash;8000). Optionally save the size with a name for future runs.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number" min="32" max="8000"
                    value={customW}
                    onChange={(e) => setCustomW(e.target.value)}
                    placeholder="Width (px)"
                    data-testid="catalog-custom-width"
                    className="bg-[#141414] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
                  />
                  <input
                    type="number" min="32" max="8000"
                    value={customH}
                    onChange={(e) => setCustomH(e.target.value)}
                    placeholder="Height (px)"
                    data-testid="catalog-custom-height"
                    className="bg-[#141414] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Name (optional — required only to save)"
                  data-testid="catalog-custom-name"
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="catalog-custom-use-once"
                    onClick={() => {
                      const w = parseInt(customW, 10);
                      const h = parseInt(customH, 10);
                      if (!w || !h || w < 32 || h < 32 || w > 8000 || h > 8000) {
                        toast.error("Both dimensions must be between 32 and 8000 px.");
                        return;
                      }
                      const spec = `custom:${w}x${h}`;
                      setFmt(spec);
                      setExtraFmts((prev) => prev.filter((k) => k !== spec));
                      setCustomOpen(false);
                      toast.success(`Custom ${w}×${h} set as primary format.`);
                    }}
                    className="flex-1 text-[11px] px-3 py-1.5 rounded-md border border-white/15 bg-white/5 text-white/85 hover:bg-white/10 hover:text-white transition"
                  >
                    Use once
                  </button>
                  <button
                    type="button"
                    data-testid="catalog-custom-save"
                    disabled={customSavePending}
                    onClick={async () => {
                      const w = parseInt(customW, 10);
                      const h = parseInt(customH, 10);
                      const nm = (customName || "").trim();
                      if (!nm) { toast.error("Give the size a name to save it."); return; }
                      if (!w || !h || w < 32 || h < 32 || w > 8000 || h > 8000) {
                        toast.error("Both dimensions must be between 32 and 8000 px.");
                        return;
                      }
                      setCustomSavePending(true);
                      try {
                        const saved = await onSaveCustomFormat?.({ name: nm, width: w, height: h, crop: "cover" });
                        if (saved) {
                          setFmt(saved.spec);
                          setCustomName(""); setCustomW(""); setCustomH("");
                          setCustomOpen(false);
                          toast.success(`Saved "${saved.name}" — set as primary format.`);
                        }
                      } catch (err) {
                        toast.error(formatApiError(err, "Save failed."));
                      } finally {
                        setCustomSavePending(false);
                      }
                    }}
                    className="flex-1 text-[11px] px-3 py-1.5 rounded-md bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-60"
                  >
                    {customSavePending ? "Saving…" : "Save & use"}
                  </button>
                </div>
                {customFormats.length > 0 && (
                  <div className="pt-1.5 border-t border-white/5">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-white/35 mb-1">Saved sizes</p>
                    <div className="flex flex-wrap gap-1">
                      {customFormats.map((c) => (
                        <div key={c.id} className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 text-[10.5px] text-white/70 group">
                          <button
                            type="button"
                            onClick={() => { setFmt(c.spec); setCustomOpen(false); toast.success(`Set "${c.name}" as primary format.`); }}
                            data-testid={`catalog-custom-pick-${c.id}`}
                            className="hover:text-white"
                          >
                            {c.name} · {c.width}×{c.height}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm(`Delete saved size "${c.name}"?`)) return;
                              try { await onDeleteCustomFormat?.(c.id); toast.success(`Deleted "${c.name}".`); }
                              catch (err) { toast.error(formatApiError(err, "Delete failed.")); }
                            }}
                            data-testid={`catalog-custom-delete-${c.id}`}
                            title="Delete saved size"
                            className="text-white/40 hover:text-red-400 transition"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Quality</label>
            <select
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
              data-testid="catalog-quality-select"
              className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
            >
              {presets.qualities.map((q) => (
                <option key={q.key} value={q.key}>{q.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Placement mode toggle — controls whether Gemini decides position
            on its own ("Auto") or the designer pre-positions the jewellery
            on each scene in the GuidedPlaceDialog before compose ("Guided").
            Works for single, batch, and multi-format. */}
        {sceneType === "model" && (
          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">
              Placement
            </label>
            <div className="flex p-0.5 rounded-md border border-white/10 bg-[#141414]" data-testid="catalog-placement-toggle">
              {[
                { v: "auto",   label: "Auto-place",    hint: "Let AI decide position & size — fastest" },
                { v: "guided", label: "Guided place",  hint: "Drag · scale · rotate the jewellery onto each scene first" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setPlacementMode(opt.v)}
                  data-testid={`catalog-placement-${opt.v}`}
                  title={opt.hint}
                  className={`flex-1 text-xs px-2 py-2 rounded transition ${
                    placementMode === opt.v
                      ? "bg-[#D4AF37]/15 text-[#D4AF37] font-medium"
                      : "text-white/55 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {placementMode === "guided" && (
              <div className="text-[10px] text-white/45 mt-1 leading-snug">
                You&apos;ll position the jewellery on each scene before AI photoreal-finalises.
                Lock-in: position, size, rotation. AI handles: lighting, shadows, contact.
              </div>
            )}
          </div>
        )}

        {multiMode && (
          <div data-testid="catalog-multi-fmts">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                Also deliver as
              </label>
              <span className="text-[10px] text-white/40 tabular-nums" data-testid="catalog-multi-count">
                {selectedFmts.length}/{maxFmts}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ...presets.formats.filter((p) => p.key !== fmt),
                ...customFormats
                  .filter((c) => c.spec !== fmt)
                  .map((c) => ({ key: c.spec, label: `${c.name}`, w: c.width, h: c.height, _custom: true })),
              ].map((p) => {
                const checked = extraFmts.includes(p.key);
                const atCap = !checked && selectedFmts.length >= maxFmts;
                return (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => toggleExtraFmt(p.key)}
                    disabled={atCap}
                    data-testid={`catalog-fmt-chip-${p.key}`}
                    className={`text-left text-[11px] px-2.5 py-2 rounded-md border transition flex items-center gap-2 ${
                      checked
                        ? "border-[#D4AF37]/55 bg-[#D4AF37]/10 text-white"
                        : atCap
                        ? "border-white/5 bg-white/[0.015] text-white/25 cursor-not-allowed"
                        : "border-white/10 bg-[#141414] text-white/70 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-sm border flex items-center justify-center shrink-0 ${
                      checked ? "border-[#D4AF37] bg-[#D4AF37]" : "border-white/30"
                    }`}>
                      {checked && <Check className="w-2.5 h-2.5 text-black" />}
                    </span>
                    <span className="truncate">
                      {p._custom ? <span className="text-[#D4AF37] mr-1">★</span> : null}
                      {p.label} <span className="text-white/40">{p.w}×{p.h}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10.5px] text-white/35 mt-1.5 leading-snug">
              One Gemini call, multiple deliverables. Total cost stays at{" "}
              <span className="text-white/65">{presets.cost_credits} credits</span>.
            </p>
          </div>
        )}

        {activeFolderObj && (
          <div className="text-[11px] text-white/45 flex items-center gap-1.5" data-testid="catalog-compose-target">
            <FolderOpen className="w-3 h-3 text-[#D4AF37]" />
            Will land in <span className="text-white/75">{activeFolderObj.name}</span>
          </div>
        )}

        <button
          onClick={onCompose}
          disabled={composing}
          data-testid="catalog-compose-btn"
          className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition disabled:opacity-60"
        >
          {composing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {composing
            ? "Composing…"
            : batchMode
              ? selectedFmts.length > 1
                ? `Compose ${sceneFiles.length} scenes × ${selectedFmts.length} formats · ${totalCost} credits`
                : `Compose ${sceneFiles.length} scenes · ${totalCost} credits`
              : selectedFmts.length > 1
                ? `Compose ${selectedFmts.length} formats · ${presets.cost_credits} credits`
                : `Compose · ${presets.cost_credits} credits`}
        </button>
      </div>
    </div>
  );
}
